import { sv } from 'date-fns/locale';
import { ReactElement, RefObject } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { PostListItem, ReactionStats } from 'api';
import birthdayPost from 'assets/images/birthdayPost.png';
import { useCompanyDetails } from 'components/Dialogs/hooks';
import { PostTopBar } from 'components/PostTopBar';
import { ReactionsWrapper } from 'components/ReactionsWrapper';
import { MONTH_DAY_SHORT } from 'constants/dateFormats';
import { useSessionState } from 'hooks';
import { InViewItemRef, Language } from 'types';
import { formattedDate } from 'utils';

import { Container, LeftSide, LinkW, PostContentWrapper, PostTitle, PostW } from '../styled';
import { BirthdayDescription, BirthdayImage } from './styled';

interface BirthdayPostProps {
  id: string;
  author: PostListItem['author'];
  authorName: string;
  birthDate?: string;
  publishedAt?: string;
  reactions?: ReactionStats;
  comments_count?: number;
  postRef: RefObject<HTMLDivElement>;
  lastItemRef?: InViewItemRef;
  onNavigateToNewUser: () => void;
  handlePostClick?: () => void;
  handleOpenReactionModal?: () => void;
  isSlider?: boolean;
}

export function BirthdayPost({
  id,
  author,
  authorName,
  birthDate,
  publishedAt,
  reactions,
  comments_count,
  postRef,
  lastItemRef,
  onNavigateToNewUser,
  handlePostClick,
  handleOpenReactionModal,
  isSlider,
}: BirthdayPostProps): ReactElement {
  const { t } = useTranslation();

  const [state] = useSessionState('i18nextLng');
  const isSwedish = state === Language.swedish;
  const locales = isSwedish ? sv : undefined;
  const { company } = useCompanyDetails();

  const formattedBirthDate = birthDate
    ? formattedDate(new Date(birthDate), MONTH_DAY_SHORT, locales)
    : '';

  return (
    <Container ref={postRef}>
      <PostW ref={lastItemRef} isAutomatedPost isSlider={isSlider}>
        <PostContentWrapper>
          <PostTopBar
            id={id}
            publishedAt={publishedAt}
            postTitle={t('post.listItem.birthdayPost.title', {
              user: authorName,
            })}
            authorName={company?.name || ''}
            author={author}
            isAutomatedPost
          />
          <LeftSide>
            <BirthdayImage src={birthdayPost} alt="birthday post image" />
            <PostTitle>
              {t('post.listItem.birthdayPost.title', {
                user: authorName,
              })}
            </PostTitle>
            <BirthdayDescription>
              <Trans
                i18nKey={
                  formattedBirthDate
                    ? 'post.listItem.birthdayPost.description'
                    : 'post.listItem.birthdayPost.oldDescription'
                }
                values={{
                  authorName,
                  formattedBirthDate,
                }}
              >
                {' '}
                It&quote;s
                <LinkW
                  onClick={(e: { stopPropagation: () => void }) => {
                    e.stopPropagation();
                    onNavigateToNewUser();
                  }}
                >
                  {authorName}
                </LinkW>
                birthday today! Congratulations! 🥳
              </Trans>
            </BirthdayDescription>
          </LeftSide>
        </PostContentWrapper>
        <ReactionsWrapper
          id={id}
          isPreview={false}
          reactions={reactions}
          comments_count={comments_count}
          handlePostClick={handlePostClick}
          handleOpenReactionModal={handleOpenReactionModal}
        />
      </PostW>
    </Container>
  );
}
